import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { MatTableDataSource } from "@angular/material/table";
import { DualListComponent } from "angular-dual-listbox";
import { Subject, forkJoin, of } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { TableInteractiveComponent } from "@sharedV11/components/tables/table-interactive/table-interactive.component";
import { CustomanDualListComponent } from "@sharedV11/components/customanduallist/customan-dual-list.component";
import { AccessControlKuroiService } from "@sharedV11/services/api/access-control-kuroi.service";
import { GlobalServicCentros } from "@sharedV11/services/global/global.service centros";
import { GlobalService } from "@sharedV11/services/global/global.service";
import { HttpErrorsService } from "@sharedV11/services/alerts/errors/http-errors.service";
import { SnackbarInfoService } from "@sharedV11/services/alerts/snackbar-info/snackbar-info.service";
import { PanelService } from "@sharedV11/services/panel/panel.service";
import { Translator } from "@sharedV11/services/translator/translator.service";
import {
  AccessControlKuroi,
  AccessControlConexionConfig,
  AccessControlKuroiFilters,
  AccessControlKuroiPayload,
  AccessControlTipoControl,
  AccessControlValidationConfig,
} from "@sharedV11/types/access-control-kuroi.model";

@Component({
  selector: "app-edit-access-control-kuroi",
  templateUrl: "./edit-access-control-kuroi.component.html",
  styleUrls: ["./edit-access-control-kuroi.component.scss"],
})
export class EditAccessControlKuroiComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() pkId: string;
  @Input() mode: "I" | "U" | "D" = "I";
  @Input() modificar: boolean;
  @Input() sourceRecord: AccessControlKuroi;

  @ViewChild("ti_calendarioDias")
  ti_calendarioDias: TableInteractiveComponent;
  @ViewChild("ti_calendarioHoras")
  ti_calendarioHoras: TableInteractiveComponent;
  @ViewChild("tiposControlDualList")
  tiposControlDualList: CustomanDualListComponent;
  @ViewChild("tipoControlEditorContainer")
  tipoControlEditorContainer: ElementRef<HTMLDivElement>;

  loading = true;
  translation: any;
  form: FormGroup;
  validationForm: FormGroup;
  databaseForm: FormGroup;
  submitted = false;
  format = {
    add: "Añadir",
    addIcon: "chevron_right",
    remove: "Borrar",
    removeIcon: "chevron_left",
    all: "Todos",
    none: "Ninguno",
    direction: DualListComponent.LTR,
    draggable: true,
    locale: "es",
  };

  centrosDisponibles = [];
  centrosAsignados = [];
  tiposControlDisponibles: AccessControlTipoControl[] = [];
  tiposControlAsignados: AccessControlTipoControl[] = [];
  tiposControlCatalog: AccessControlTipoControl[] = [];
  tiposControlDataSource = new MatTableDataSource<AccessControlTipoControl>([]);

  calendarioDiasDataSource = new MatTableDataSource([]);
  calendarioHorasDataSource = new MatTableDataSource([]);
  lugares: { pkId: string; Nombre: string }[] = [];

  tipoControlEditorVisible = false;
  tipoControlEnEdicion: AccessControlTipoControl | null = null;
  tipoControlEditorIndex = -1;

 
  mostrarDualListTiposControl = false;
  mostrarValidaciones = false;


  private dualListReady = false;
  private pendingRefresh = false;

  private destroy$ = new Subject<void>();
  private isPatchingRecord = false; 
  private readonly CLAVE_PATTERN = /^[A-Za-z0-9.\-]+$/;
  private readonly DESCRIPCION_PATTERN = /^[A-Za-zÀ-ÿ0-9 .,;:!?¿¡()\[\]{}\-_@#$%&*+=|\\/"'`~<>\^.\n\r]*$/;
  private readonly SQL_SERVER_PATTERN = /^[A-Za-z0-9.,]+$/;
  private readonly SQL_DATABASE_PATTERN = /^[A-Za-z0-9_!@]+$/;
  private readonly SQL_USER_PATTERN = /^[A-Za-z0-9]+$/;
  passwordHidden = true;

  constructor(
    private fb: FormBuilder,
    private panelService: PanelService,
    private translator: Translator,
    private snackbar: SnackbarInfoService,
    private httpErrorService: HttpErrorsService,
    private globales: GlobalService,
    private gsCentros: GlobalServicCentros,
    private accessControlService: AccessControlKuroiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.setupTranslations();
    this.initializeForms();
    this.registerClaseWatcherParaVisibilidade(); 
    this.loadCentrosAndLugares(); 
    this.loadRecord(); 
    this.registerDirtyWatcher();
  }

  label(key: string, fallback?: string): string {
    const value = this.translation?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
    return fallback ?? key;
  }


  private registerClaseWatcherParaVisibilidade(): void {
    const claseControl = this.form.get("Clase");
    if (!claseControl) {
      return;
    }

    const updateVisibility = (value: any) => {
      const claseNormalizada = (value ?? "").toString().trim().toUpperCase();
      const isKuroi = claseNormalizada === "KUROI";
      
      this.mostrarDualListTiposControl = isKuroi;
      this.mostrarValidaciones = isKuroi;
    };

    claseControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(updateVisibility);

    updateVisibility(claseControl.value);
  }

  ngAfterViewInit(): void {

    this.dualListReady = true;
    
    if (this.pendingRefresh) {
      this.pendingRefresh = false;
      this.refreshDualList();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  get vf(): { [key: string]: AbstractControl } {
    return this.validationForm.controls;
  }

  get df(): { [key: string]: AbstractControl } {
    return this.databaseForm.controls;
  }

  private setupTranslations(): void {
    this.translation = this.translator.GetTranslations();
    this.format = {
      add: this.translation.Anadir,
      addIcon: "chevron_right",
      remove: this.translation.Borrar,
      removeIcon: "chevron_left",
      all: this.translation.Todos,
      none: this.translation.Ninguno,
      direction: DualListComponent.LTR,
      draggable: true,
      locale: "es",
    };
  }

  private initializeForms(): void {
    this.form = this.fb.group({
      pkId: [""],
      Nombre: ["", [Validators.required, Validators.maxLength(100)]],
      Clave: [
        "",
        [
          Validators.required,
          // La base de datos limita el campo Clave a 20 caracteres.
          // Mantenemos la misma restricción en el formulario.
          Validators.maxLength(20),
          Validators.pattern(this.CLAVE_PATTERN),
        ],
      ],
      Descripcion: [
        "",
        [Validators.maxLength(250), Validators.pattern(this.DESCRIPCION_PATTERN)],
      ],
      LugarId: ["", Validators.required],
      Clase: ["", Validators.required],
      TipoEntSal: ["", Validators.required],
      TipoAcceso: ["", Validators.required],
      TipoConexion: ["", Validators.required],
      EsSQLCentralizada: [null],
      DeBaja: [false],
      CentrosIds: [""],
      CentrosNombres: [""],
    });

    this.validationForm = this.fb.group({
      CHMargenInferior: [0, [Validators.min(0)]],
      CHMargenSuperior: [0, [Validators.min(0)]],
      ValidacionEstandar: [false],
      ValidacionInternet: [false],
      NumPasosPrevios: [0, [Validators.min(0)]],
      GuardarFichajesIncorrectos: [false],
      PermitirLocInexistentes: [false],
    });

    this.databaseForm = this.fb.group({
      NombreProyecto: ["CAKUROI", Validators.required],
      SQLServer: ["", [Validators.pattern(this.SQL_SERVER_PATTERN)]],
      SQLDataBase: ["", [Validators.pattern(this.SQL_DATABASE_PATTERN)]],
      SQLUser: ["", [Validators.pattern(this.SQL_USER_PATTERN)]],
      SQLPassword: [""],
    });
  }

  
  private async loadCentrosAndLugares(): Promise<void> {
    await this.gsCentros.getCentros();
    this.centrosDisponibles = this.sortByName(
      this.dedupeByKey(
        this.gsCentros
          .filtrarTiposCentros()
          .map((centro) => ({ ...centro })),
        "pkId"
      )
    );

    this.accessControlService
      .getLugares()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (lugares) => {
          this.lugares = this.sortByName(lugares ?? []);
        },
        error: (error) => this.httpErrorService.identificarErrores(error),
      });
  }

  
  private loadTiposControlCatalog(): void {
    this.accessControlService
      .getTiposControlListado()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tipos) => {
          this.tiposControlCatalog = [...(tipos ?? [])];
          
          this.tiposControlDisponibles = this.tiposControlCatalog.map(item => ({
            ...item,
            _name: item.Nombre || item.CodigoTipoControl || 'Sin nombre',
          }));
        },
        error: (error) => this.httpErrorService.identificarErrores(error),
      });
  }

  private loadRecord(): void {
    if (this.sourceRecord) {
      this.loadRecordFromSource(this.sourceRecord);
      return;
    }

    if (!this.pkId || this.mode === "I") {
      this.loadTiposControlCatalog();
      this.submitted = false;
      this.loading = false;
      return;
    }

    forkJoin({
      catalog: this.accessControlService.getTiposControlListado(),
      record: this.accessControlService.getAccesoKuroiById(this.pkId)
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ catalog, record: registro }) => {
          this.tiposControlCatalog = [...(catalog ?? [])];
          
          this.tiposControlDisponibles = this.tiposControlCatalog.map(item => ({
            ...item,
            _name: item.Nombre || item.CodigoTipoControl || 'Sin nombre',
          }));

          if (!registro) {
            this.submitted = false;
            this.loading = false;
            return;
          }

          if (this.mode === "D") {
            registro.pkId = null;
            this.mode = "I";
          }

          this.patchRecord(registro);
          this.submitted = false;
          this.loading = false;
        },
        error: (error) => {
          this.httpErrorService.identificarErrores(error);
          this.submitted = false;
          this.loading = false;
        },
      });
  }

  private loadRecordFromSource(record: AccessControlKuroi): void {
    const pkId =
      record?.pkId ??
      (record as any)?.PkId ??
      (record as any)?.pkID ??
      (record as any)?.pkid ??
      null;

    forkJoin({
      catalog: this.accessControlService.getTiposControlListado(),
      record: pkId 
        ? this.accessControlService.getAccesoKuroiById(String(pkId))
        : of(record)
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ catalog, record: registro }) => {
          this.tiposControlCatalog = [...(catalog ?? [])];

          this.tiposControlDisponibles = this.tiposControlCatalog.map(item => ({
            ...item,
            _name: item.Nombre || item.CodigoTipoControl || 'Sin nombre',
          }));

          const recordToClone = registro ?? record;
          this.cloneRecord(recordToClone);
          this.submitted = false;
          this.loading = false;
        },
        error: (error) => {
          this.httpErrorService.identificarErrores(error);
      
          this.accessControlService
            .getTiposControlListado()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (tipos) => {
                this.tiposControlCatalog = [...(tipos ?? [])];
                this.cloneRecord(record);
              },
              error: () => this.cloneRecord(record)
            });
          this.submitted = false;
          this.loading = false;
        },
      });
  }

  private patchRecord(registro: AccessControlKuroi): void {

    this.isPatchingRecord = true;
    
    const esSqlCentralizada = this.normalizeSqlFlag(registro.EsSQLCentralizada);
    const resolvedPkId =
      registro.pkId ??
      (registro as any)?.PkId ??
      (registro as any)?.pkID ??
      (registro as any)?.pkid ??
      null;

    this.form.patchValue({
      pkId: resolvedPkId,
      Nombre: registro.Nombre,
      Clave: registro.Clave,
      Descripcion: registro.Descripcion,
      LugarId: registro.Lugar ?? registro.LugarId,
      Clase: registro.Clase,
      TipoEntSal: registro.TipoEntSal,
      TipoAcceso: registro.TipoAcceso,
      TipoConexion: this.normalizeTipoConexionValue(registro.TipoConexion),
      EsSQLCentralizada: esSqlCentralizada,
      DeBaja: registro.DeBaja === "1" || registro.DeBaja === true,
      CentrosIds: registro.CentrosIds,
      CentrosNombres: registro.CentrosNombres,
    });

    if (registro.Configuraciones) {
      this.validationForm.patchValue({
        CHMargenInferior: registro.Configuraciones.CHMargenInferior ?? 0,
        CHMargenSuperior: registro.Configuraciones.CHMargenSuperior ?? 0,
        ValidacionEstandar: this.normalizeBooleanFlag(
          registro.Configuraciones.ValidacionEstandar
        ),
        ValidacionInternet: this.normalizeBooleanFlag(
          registro.Configuraciones.ValidacionInternet
        ),
        NumPasosPrevios: registro.Configuraciones.NumPasosPrevios ?? 0,
        GuardarFichajesIncorrectos: this.normalizeBooleanFlag(
          registro.Configuraciones.GuardarFichajesIncorrectos
        ),
        PermitirLocInexistentes: this.normalizeBooleanFlag(
          registro.Configuraciones.PermitirLocInexistentes
        ),
      });
    }

    const conexion: AccessControlConexionConfig = registro.Conexion ?? {
      SQLServer: "",
      SQLDataBase: "",
      SQLUser: "",
      SQLPassword: "",
      SQLPwd: "",
    };

    const rawSqlDb =
      (registro.SQLDataBase as string | undefined) ??
      (conexion.SQLDataBase as string | undefined) ??
      "";
    const normalizedSqlDb =
      typeof rawSqlDb === "string" ? rawSqlDb.trim() : "";

    this.databaseForm.patchValue({
      NombreProyecto: registro.NombreProyecto ?? "CAKUROI",
      SQLServer: registro.SQLServer ?? conexion.SQLServer ?? "",
      SQLDataBase: normalizedSqlDb,
      SQLUser: registro.SQLUser ?? conexion.SQLUser ?? "",
      SQLPassword:
        registro.SQLPwd ??
        conexion.SQLPwd ??
        conexion.SQLPassword ??
        "",
    });

    this.centrosAsignados = this.gsCentros.daTiposCentros({
      CentrosIds: registro.CentrosIds,
      CentrosNombres: registro.CentrosNombres,
    });
    this.removeCentrosFromDisponibles();

    this.tiposControlAsignados =
      registro.ListadoTiposControl?.map((item) => ({
        ...item,
        _name: item.Nombre || item.CodigoTipoControl || 'Sin nombre',
        updated: false,
      })) ?? [];

    this.updateTiposDataSource();
    this.updateCentrosValues();
    
    const claseNormalizada = (registro.Clase ?? "").toString().trim().toUpperCase();
    const isKuroi = claseNormalizada === "KUROI";
    this.mostrarDualListTiposControl = isKuroi;
    this.mostrarValidaciones = isKuroi;
  
    this.isPatchingRecord = false;
  }

  private cloneRecord(registro: AccessControlKuroi): void {
    const cloned = { 
      ...registro,
     
      ListadoTiposControl: registro.ListadoTiposControl?.map(tipo => ({
        ...tipo,
        _name: tipo.Nombre || tipo.CodigoTipoControl || 'Sin nombre',
        Configuraciones: tipo.Configuraciones ? { ...tipo.Configuraciones } : undefined,
        ListadoTiposControlCalendario: tipo.ListadoTiposControlCalendario 
          ? [...tipo.ListadoTiposControlCalendario] 
          : null,
        ListadoTiposControlCalendarioHorario: tipo.ListadoTiposControlCalendarioHorario
          ? [...tipo.ListadoTiposControlCalendarioHorario]
          : null,
      })) ?? [],
    };

    cloned.pkId = undefined;
    cloned.Nombre = `${registro.Nombre ?? ""} (${this.label(
      "Duplicado",
      "Duplicado"
    )})`;
    cloned.Clave = `${registro.Clave ?? ""}-copy`;

    const tipoAccesoOriginal = (registro.TipoAcceso ?? "").toString().toUpperCase();
    cloned.TipoAcceso =
      tipoAccesoOriginal === "LOCAL" || tipoAccesoOriginal === "REMOTO"
        ? tipoAccesoOriginal
        : "LOCAL";

    this.patchRecord(cloned);
    this.pkId = undefined;
    this.mode = "I";
    this.panelService.setDiscardChanges(false);
  }

  private registerDirtyWatcher(): void {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.panelService.setDiscardChanges(false);
    });
    this.validationForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.panelService.setDiscardChanges(false);
      });
    this.databaseForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.panelService.setDiscardChanges(false);
      });
  }

  private updateCentrosValues(): void {
    const ids = this.centrosAsignados
      .map((centro) => centro.pkId)
      .filter((id) => !!id);
    this.form.patchValue(
      {
        CentrosIds: ids.join(","),
        CentrosNombres: this.centrosAsignados
          .map((centro) => centro.Nombre)
          .join(", "),
      },
      { emitEvent: false }
    );
  }

  private removeCentrosFromDisponibles(): void {
    const asignadosIds = new Set(
      this.centrosAsignados.map((centro) => centro.pkId)
    );
    this.centrosDisponibles = this.sortByName(
      this.centrosDisponibles.filter(
        (centro) => !asignadosIds.has(centro.pkId)
      )
    );
  }

  private updateTiposDataSource(): void {
    this.tiposControlDataSource.data = [
      ...this.tiposControlAsignados.map((tipo) => ({
        ...tipo,
        ComprobacionesLabel: this.buildComprobacionesLabel(tipo),
      })),
    ];
  }

  private refreshDualList(): void {
    this.tiposControlDisponibles = [...this.tiposControlDisponibles];
    this.tiposControlAsignados = [...this.tiposControlAsignados];

    this.cdr.detectChanges();

    if (!this.dualListReady || !this.tiposControlDualList) {
      this.pendingRefresh = true;
      return;
    }

    setTimeout(() => {
      const dual: any = this.tiposControlDualList;
      
      if (dual && dual.available && dual.confirmed) {
        dual.available.list = [...this.tiposControlDisponibles];
        dual.confirmed.list = [...this.tiposControlAsignados];
   
        if (typeof dual.onFilter === 'function') {
          dual.onFilter(dual.available);
          dual.onFilter(dual.confirmed);
        }
      }
    }, 100);
  }

  private buildComprobacionesLabel(
    tipo: Partial<AccessControlTipoControl>
  ): string {
    const labels: string[] = [];
    if (tipo.CompHoraria) {
      labels.push(this.label("Horario", "Horario"));
    }
    if (tipo.CompDiaria) {
      labels.push(this.label("Diario", "Diario"));
    }
    if (tipo.CompIntervalo) {
      labels.push(this.label("Intervalo", "Intervalo"));
    }
    if (tipo.CompDiaSemana) {
      labels.push(this.label("DiaSemana", "Día Semana"));
    }
    if (tipo.CompPasoPrevio) {
      labels.push(this.label("PasoPrevio", "Paso previo"));
    }
    if (tipo.CompCalendario) {
      labels.push(this.label("Calendario", "Calendario"));
    }
    if (tipo.CompCalendHorario) {
      labels.push(this.label("CalendHorario", "Calendario horario"));
    }
    if (tipo.DenegarAcceso) {
      labels.push(this.label("DenegarAcceso", "Denegar acceso"));
    }
    return labels.join(", ");
  }

  submit(cerrarPanel = true): void {
    this.submitted = true;
    
    const claseAtual = (this.form.get("Clase")?.value ?? "").toString().trim().toUpperCase();
    const isKuroi = claseAtual === "KUROI";
    
    if (
      this.form.invalid ||
      (isKuroi && this.validationForm.invalid) ||
      this.databaseForm.invalid
    ) {
      this.snackbar.openSnackBar(
        this.label("ValidarCampos", "Revise los campos obligatorios"),
        "",
        "red-snackbar"
      );
      this.form.markAllAsTouched();
      if (isKuroi) {
        this.validationForm.markAllAsTouched();
      }
      this.databaseForm.markAllAsTouched();
      return;
    }

    if (isKuroi && !this.tiposControlAsignados.length) {
      this.snackbar.openSnackBar(
        this.label(
          "TiposControlRequeridos",
          "Debe asignar al menos un Tipo de Control"
        ),
        "",
        "red-snackbar"
      );
      return;
    }

    const payload = this.buildPayload();
    this.loading = true;

    const request$ =
      this.mode === "I" || this.mode === "D"
        ? this.accessControlService.insertAccesoKuroi(payload)
        : this.accessControlService.updateAccesoKuroi(payload);

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        if (response?.DatosResult) {
          this.snackbar.openSnackBar(
            this.label(
              "DatosGuardadosCorrectamente",
              "Datos guardados correctamente"
            ),
            "",
            "green-snackbar"
          );
          this.panelService.setSavePanel(cerrarPanel);
          if (cerrarPanel) {
            this.panelService.setClosePanel(null);
          } else {
            this.mode = "U";
            this.pkId = response.DatosResult.pkId ?? this.pkId;
            this.loadRecord();
          }
          this.submitted = false;
        } else {
          this.handleApiMessages(response?.Mensajes);
        }
        this.loading = false;
      },
      error: (error) => {
        this.httpErrorService.identificarErrores(error);
        this.loading = false;
      },
    });
  }

  delete(): void {
    if (!this.pkId) {
      return;
    }
    this.loading = true;
    const clave = this.form.get("Clave")?.value ?? "";
    this.accessControlService
      .deleteAccesoKuroi(clave)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response?.DatosResult !== null) {
            this.snackbar.openSnackBar(
              this.label(
                "DatosBorradosCorrectamente",
                "Datos borrados correctamente"
              ),
              "",
              "green-snackbar"
            );
            this.panelService.setClosePanel(null);
          } else {
            this.handleApiMessages(response?.Mensajes);
          }
          this.loading = false;
        },
        error: (error) => {
          this.httpErrorService.identificarErrores(error);
          this.loading = false;
        },
      });
  }

  discard(): void {
    this.submitted = false;
    if (this.pkId) {
      this.loadRecord();
    } else {
      this.form.reset({
        Nombre: "",
        Clave: "",
        Descripcion: "",
        LugarId: "",
        Clase: "",
        TipoEntSal: "",
        TipoAcceso: "",
        TipoConexion: "",
        EsSQLCentralizada: "0",
        DeBaja: false,
        CentrosIds: "",
        CentrosNombres: "",
      });
      this.validationForm.reset({
        CHMargenInferior: 0,
        CHMargenSuperior: 0,
        ValidacionEstandar: false,
        ValidacionInternet: false,
        NumPasosPrevios: 0,
        GuardarFichajesIncorrectos: false,
        PermitirLocInexistentes: false,
      });
      this.databaseForm.reset({
        NombreProyecto: "CAKUROI",
        SQLServer: "",
        SQLDataBase: "",
        SQLUser: "",
        SQLPassword: "",
      });
      this.centrosAsignados = [];
      this.tiposControlAsignados = [];
      this.tiposControlDisponibles = [];
      this.updateTiposDataSource();
      this.refreshDualList();
      this.centrosDisponibles = [];
      this.loadCentrosAndLugares();
    }
    this.panelService.setDiscardChanges(true);
  }

  destroy(cerrarPanel = true): void {
    this.submitted = false;
    this.panelService.setDiscardChanges(true);
    this.panelService.setSavePanel(cerrarPanel);
    if (cerrarPanel) {
      this.panelService.setClosePanel(null);
    }
  }

  addCentros(event: any): void {
    const selection = this.normalizeSelection(event);
    if (!selection.length) {
      return;
    }
    this.centrosAsignados = this.sortByName(
      this.dedupeByKey(
        [
          ...this.centrosAsignados,
          ...selection.map((item) => ({ ...item })),
        ],
        "pkId"
      )
    );
    this.removeCentrosFromDisponibles();
    this.updateCentrosValues();
    this.panelService.setDiscardChanges(false);
  }

  removeCentros(event: any): void {
    const selection = this.normalizeSelection(event);
    if (!selection.length) {
      return;
    }
    const removeIds = new Set(selection.map((item) => item.pkId));
    this.centrosDisponibles = this.sortByName(
      this.dedupeByKey(
        [
          ...this.centrosDisponibles,
          ...this.centrosAsignados.filter((item) => removeIds.has(item.pkId)),
        ],
        "pkId"
      )
    );
    this.centrosAsignados = this.centrosAsignados.filter(
      (item) => !removeIds.has(item.pkId)
    );
    this.updateCentrosValues();
    this.panelService.setDiscardChanges(false);
  }


  private normalizeSelection(event: any): any[] {
    if (Array.isArray(event)) {
      return event;
    }
    if (event?.detail) {
      if (Array.isArray(event.detail)) {
        return event.detail;
      }
      if (Array.isArray(event.detail.items)) {
        return event.detail.items;
      }
      if (Array.isArray(event.detail.value)) {
        return event.detail.value;
      }
    }
    return [];
  }

  editTipoControl(tipo: AccessControlTipoControl | any): void {
    const codigo = this.resolveTipoControlId(tipo);
    if (!codigo) {
      return;
    }
    const index = this.tiposControlAsignados.findIndex(
      (item) => this.resolveTipoControlId(item) === codigo
    );
    this.tipoControlEditorIndex = index;
    const current =
      index >= 0 ? this.tiposControlAsignados[index] : null;
    if (!current) {
      return;
    }
    this.tipoControlEnEdicion = this.cloneTipoControl(current);
    this.tipoControlEditorVisible = true;

    setTimeout(() => {
      if (this.tipoControlEditorContainer?.nativeElement) {
        this.tipoControlEditorContainer.nativeElement.scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
      }
    });
  }

  onTipoControlEditorSave(updated: AccessControlTipoControl): void {
        if (!updated) {
      this.onTipoControlEditorCancel();
          return;
        }
    const codigo = this.resolveTipoControlId(updated);
    const index =
      this.tipoControlEditorIndex >= 0
        ? this.tipoControlEditorIndex
        : this.tiposControlAsignados.findIndex(
          (item) => this.resolveTipoControlId(item) === codigo
        );
        if (index >= 0) {
          const nextNombre =
            updated.Nombre ??
            this.tiposControlAsignados[index].Nombre ??
            (this.tiposControlAsignados[index] as any)._name ??
            "";
          const merged: AccessControlTipoControl = {
            ...this.tiposControlAsignados[index],
            ...updated,
            CodigoTipoControl:
              codigo ?? this.tiposControlAsignados[index].CodigoTipoControl,
            updated: true,
          };
          this.tiposControlAsignados[index] = merged;
          (this.tiposControlAsignados[index] as any)._name = nextNombre;
          this.updateTiposDataSource();
          this.panelService.setDiscardChanges(false);
        }
    this.onTipoControlEditorCancel();
  }

  onTipoControlEditorCancel(): void {
    this.tipoControlEditorVisible = false;
    this.tipoControlEnEdicion = null;
    this.tipoControlEditorIndex = -1;
  }

  private cloneTipoControl(
    tipo: AccessControlTipoControl
  ): AccessControlTipoControl {
    return JSON.parse(JSON.stringify(tipo ?? {}));
  }

  private resolveTipoControlId(record: any): string | undefined {
    if (!record) {
      return undefined;
    }
    return (
      record.CodigoTipoControl ??
      record.Codigo ??
      record._id ??
      record.pkId ??
      undefined
    );
  }
  
  private buildPayload(): AccessControlKuroiPayload {
    const formValue = this.form.getRawValue();
    const validationValue = this.validationForm.getRawValue();
    const databaseValue = this.databaseForm.getRawValue();

    const resolvedPkId =
      this.mode === "I" || this.mode === "D"
        ? "0"
        : formValue.pkId ?? this.pkId ?? "0";

    const payload: AccessControlKuroiPayload = {
      ...formValue,
      pkId: resolvedPkId,
      TipoConexion: this.normalizeTipoConexionValue(formValue.TipoConexion),
      Lugar: formValue.Lugar ?? formValue.LugarId ?? "",
      EsSQLCentralizada: this.normalizeSqlFlag(formValue.EsSQLCentralizada),
      DeBaja: formValue.DeBaja ? 1 : 0,
      Configuraciones: {
        ...validationValue,
      },
      NombreProyecto: databaseValue.NombreProyecto,
      SQLServer: databaseValue.SQLServer,
      SQLDataBase: databaseValue.SQLDataBase,
      SQLUser: databaseValue.SQLUser,
      SQLPwd: databaseValue.SQLPassword,
      Conexion: {
        SQLServer: databaseValue.SQLServer,
        SQLDataBase: databaseValue.SQLDataBase,
        SQLUser: databaseValue.SQLUser,
        SQLPassword: databaseValue.SQLPassword,
      },
      ListadoTiposControl: this.tiposControlAsignados.map((item) => ({
        ...item,
        updated: undefined,
      })),
      CentrosIds: this.centrosAsignados.map((item) => item.pkId).join(","),
      CentrosNombres: this.centrosAsignados
        .map((item) => item.Nombre)
        .join(", "),
    };

    payload.pkId = resolvedPkId;

    return payload;
  }

  private normalizeSqlFlag(value: any): "1" | "0" {
    if (value === null || value === undefined) {
      return "0";
    }
    if (typeof value === "string") {
      const lowered = value.toLowerCase();
      if (lowered === "1" || lowered === "true" || lowered === "si") {
        return "1";
      }
      return "0";
    }
    return value === true || value === 1 ? "1" : "0";
  }

  private normalizeTipoConexionValue(value: any): string {
    if (value === null || value === undefined) {
      return "";
    }
    const normalized = value.toString().trim().toUpperCase();
    if (normalized === "REMOTA") {
      return "REMOTO";
    }
    return normalized;
  }

  private isSqlCentralizada(): boolean {
    return this.normalizeSqlFlag(this.f["EsSQLCentralizada"]?.value) === "1";
  }

  private normalizeBooleanFlag(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === "boolean") {
      return value;
    }
    if (typeof value === "number") {
      return value === 1;
    }
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "1" || normalized === "true" || normalized === "si";
    }
    return false;
  }

  getCentralizadaLabel(): string {
    const rawValue = this.f["EsSQLCentralizada"]?.value;
    if (rawValue === null || rawValue === undefined || rawValue === "") {
      return "";
    }
    return this.isSqlCentralizada()
      ? this.label("Si", "Sí")
      : this.label("No", "No");
  }

  private dedupeByKey<T extends Record<string, any>>(
    items: T[],
    key: keyof T
  ): T[] {
    const map = new Map<any, T>();
    items.forEach((item) => {
      map.set(item[key], item);
    });
    return Array.from(map.values());
  }

  private sortByName<T extends { Nombre?: string }>(items: T[]): T[] {
    return [...items].sort((a, b) =>
      (a.Nombre || "").localeCompare(b.Nombre || "", undefined, {
        sensitivity: "base",
      })
    );
  }

  private handleApiMessages(mensajes?: { DescripcionMensaje: string }[]): void {
    if (!mensajes?.length) {
      this.snackbar.openSnackBar(
        this.label("ErrorGenerico", "Se produjo un error"),
        "",
        "red-snackbar"
      );
      return;
    }
    mensajes.forEach((mensaje) => {
      this.snackbar.openSnackBar(
        mensaje.DescripcionMensaje ??
          this.label("ErrorGenerico", "Se produjo un error"),
        "",
        "red-snackbar"
      );
    });
  }
}

